import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { patientAgeLabel, esdmStatus, formatDate } from '../lib/format';
import type { Child } from '@aba-denver/core';

type SortKey = 'name' | 'recent';

export function Patients() {
  const { children, loading, getChildEsdmProgress } = useData();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');

  const compare = useMemo(() => {
    return (a: Child, b: Child) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name);
      const aDate = getChildEsdmProgress(a.id)?.updatedAt;
      const bDate = getChildEsdmProgress(b.id)?.updatedAt;
      if (!aDate && !bDate) return a.name.localeCompare(b.name);
      if (!aDate) return 1;
      if (!bDate) return -1;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    };
  }, [sortKey, getChildEsdmProgress]);

  const { mine, others } = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = children.filter((c) => c.name.toLowerCase().includes(term));
    return {
      mine: filtered.filter((c) => c.ownerId === user?.uid).sort(compare),
      others: filtered.filter((c) => c.ownerId !== user?.uid).sort(compare),
    };
  }, [children, search, user, compare]);

  const renderRows = (list: Child[]) =>
    list.map((child) => {
      const progressDoc = getChildEsdmProgress(child.id);
      const status = esdmStatus(progressDoc);
      return (
        <tr key={child.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/patients/${child.id}`)}>
          <td style={{ fontWeight: 600 }}>
            {child.name}
            {child.isPremature && (
              <span className="badge" style={{ marginLeft: 8 }}>
                Prematuro
              </span>
            )}
          </td>
          <td>{patientAgeLabel(child)}</td>
          <td>
            <Badge variant={status.variant}>{status.label}</Badge>
            {progressDoc && (
              <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
                {formatDate(progressDoc.updatedAt)}
              </span>
            )}
          </td>
          <td style={{ textAlign: 'right' }}>
            <span className="muted">›</span>
          </td>
        </tr>
      );
    });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Pacientes</h1>
          <p className="muted" style={{ margin: 0 }}>
            Todos os pacientes cadastrados pela equipe.
          </p>
        </div>
        <button className="btn" onClick={() => navigate('/patients/new')}>
          + Novo paciente
        </button>
      </div>

      <div className="card">
        <div className="row" style={{ gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Buscar por nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
            }}
          />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <option value="name">Ordenar: Nome</option>
            <option value="recent">Ordenar: Mais recentes</option>
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : mine.length === 0 && others.length === 0 ? (
          <div className="empty-state">
            {children.length === 0 ? 'Nenhum paciente cadastrado ainda.' : 'Nenhum paciente encontrado.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {mine.length > 0 && (
              <>
                {others.length > 0 && <h3 style={{ marginBottom: 8 }}>Meus pacientes</h3>}
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Idade</th>
                      <th>Checklist ESDM</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>{renderRows(mine)}</tbody>
                </table>
              </>
            )}
            {others.length > 0 && (
              <>
                {mine.length > 0 && <h3 style={{ margin: '20px 0 8px' }}>Outros pacientes</h3>}
                <table className="table">
                  {mine.length === 0 && (
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Idade</th>
                        <th>Checklist ESDM</th>
                        <th></th>
                      </tr>
                    </thead>
                  )}
                  <tbody>{renderRows(others)}</tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
